import {
    ModalTypes, ModalDefaults, ActionConfigs, Sizes, Types, FieldTypes,
    Align, Backdrop, ActionSizes, Validators, Colors, ReadOnlyStyles
} from './Constants';
import { html, escapeHtml, isTrusted, markTrusted } from '../ContentComposer/ContentComposer';
import { GenericModalBuilder, GenericActionBuilder } from './ModalBuilders';
import { fromExistingModal } from './ExistingModalHandle';
import { _initValidationListeners as _initValidationListenersFn, _validateField as _validateFieldFn, _updateFieldUI as _updateFieldUIFn, _checkFormValidity as _checkFormValidityFn, _validateForm as _validateFormFn } from './features/validation/validationManager.js';
import { _initDatepickerFields as _initDatepickerFieldsFn, _applyDatepickerFieldConfig as _applyDatepickerFieldConfigFn, _destroyDatepickerFields as _destroyDatepickerFieldsFn } from './features/datepicker/datepickerManager.js';
import { _generateFieldHtml as _generateFieldHtmlFn, _generateReadOnlyFieldHtml as _generateReadOnlyFieldHtmlFn, _renderReadonlyRow as _renderReadonlyRowFn, _getAllFields as _getAllFieldsFn } from './features/fields/fieldRenderers.js';
import { _generateFormHtml as _generateFormHtmlFn, _getFormData as _getFormDataFn, _attachFormFieldAccess as _attachFormFieldAccessFn, _populateBatchTable as _populateBatchTableFn } from './features/form/formManager.js';

export class GenericModal {

    /**
     * Fluent factory for constructing a GenericModal.
     * @param {Object} options Initial settings.
     * @returns {GenericModalBuilder}
     */
    static create(options) {
        return new GenericModalBuilder(options, GenericModal);
    }

    /**
     * Wrap a pre-rendered Bootstrap modal. Does not generate DOM.
     * bootstrap.Modal is constructed lazily on first show().
     */
    static from(target, options) {
        return fromExistingModal(target, options);
    }

    /**
     * Fluent factory for custom table actions.
     * @param {string} key
     * @returns {GenericActionBuilder}
     */
    static Action(key) {
        return new GenericActionBuilder(key);
    }

    /**
     * Static helper — generates an HTML string of action icon-buttons for use in a table's Actions column.
     */
    static renderActions(actions = [], options = {}) {
        const { size = 'sm', gap = '4px', label = false } = options;

        const validActionSizes = Object.values(ActionSizes);
        if (!validActionSizes.includes(size)) {
            console.warn(`[GenericModal] Invalid action size: "${size}". Valid sizes are: ${validActionSizes.join(', ')}`);
        }

        const sizeMap = {
            sm: { font: '13px', padding: '3px 7px',   iconSize: '14px' },
            md: { font: '14px', padding: '5px 10px',  iconSize: '15px' },
            lg: { font: '15px', padding: '7px 14px',  iconSize: '16px' }
        };
        const s = sizeMap[size] || sizeMap.sm;

        const buttons = actions.map(action => {
            const cfg = typeof action === 'string'
                ? (ActionConfigs[action.toLowerCase()] || { icon: 'bi-circle', color: '#64748b', title: action, btnClass: `gm-btn-${action}` })
                : action;

            const actionKey = typeof action === 'string' ? action : (action.key || '');
            const labelHtml = label ? html`<span style="margin-left:4px;">${cfg.title}</span>` : '';
            
            return html`
                <button type="button"
                    class="btn gm-action-btn ${cfg.btnClass}"
                    data-action="${actionKey}"
                    title="${cfg.title}"
                    style="
                        background-color: ${cfg.color};
                        color: #fff;
                        border: none;
                        padding: ${s.padding};
                        font-size: ${s.font};
                        border-radius: 6px;
                        line-height: 1;
                        cursor: pointer;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                        white-space: nowrap;
                        font-weight: 600;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    "
                    onmouseover="this.style.filter='brightness(1.1)';this.style.transform='translateY(-1px)';this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)';"
                    onmouseout="this.style.filter='none';this.style.transform='translateY(0)';this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)';"
                ><i class="bi ${cfg.icon}" style="font-size:${s.iconSize};"></i>${labelHtml}</button>`;
        });

        return html`<div style="display:inline-flex;gap:${gap};align-items:center;flex-wrap:nowrap;">${buttons}</div>`;
    }

    constructor(settings = {}) {
        this.settings = settings;

        this.processingState = {
            active: false,
            results: []
        };

        this.id = 'gm_' + Math.floor(Math.random() * 1000000);
        this.elements = {};
        this.bsModal = null;
        this.touchedFields = new Set();
        this.datepickerInstances = [];
    }

    _getAllFields(fields = null) { return _getAllFieldsFn(this, fields); }

    _getTemplate() {
            return html`
                <div id="${this.id}" class="modal fade gm-modal" tabindex="-1" role="dialog">
                    <div class="modal-dialog gm-dialog">
                        <div class="modal-content gm-content">
                            <div class="modal-header gm-header">
                                <h4 class="modal-title gm-title">
                                    <i class="modal-icon bi"></i>
                                    <span class="modal-title-text"></span>
                                </h4>
                                <button type="button" class="gm-close" data-bs-dismiss="modal" data-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body gm-body"></div>
                            <div class="modal-footer gm-footer">
                                <button type="button" class="gm-btn gm-btn-lg gm-btn-secondary gm-btn-reject">Reject</button>
                                <button type="button" class="gm-btn gm-btn-lg gm-btn-accept">Accept</button>
                            </div>
                        </div>
                    </div>
                </div>`;
        }

    show() {
        const $ = window.jQuery;
        const $modal = $(this._getTemplate()).appendTo('body');
        this.elements = {
            $modal,
            $body: $modal.find('.modal-body'),
            $dialog: $modal.find('.modal-dialog'),
            $content: $modal.find('.modal-content'),
            $header: $modal.find('.modal-header'),
            $footer: $modal.find('.modal-footer'),
            $titleText: $modal.find('.modal-title-text'),
            $icon: $modal.find('.modal-icon'),
            $acceptBtn: $modal.find('.gm-btn-accept'),
            $rejectBtn: $modal.find('.gm-btn-reject')
        };

        this._applyStyles();
        this._configureHeader();
        this._configureFooter();

        const hasFormItems = (this.settings.items && this.settings.items.length > 0) || (this.settings.fields && this.settings.fields.length > 0);
        if (hasFormItems) {
            if (this.settings.items && this.settings.items.length > 0) {
                const renderedItems = this.settings.items.map(item => {
                    if (item.type === 'field') {
                        return this._generateFieldHtml(item.field);
                    } else if (item.type === 'html') {
                        return item.html;
                    } else if (item.type === 'contentButton') {
                        return this._renderSingleContentButton(item.button);
                    } else if (item.type === 'contentButtonGroup') {
                        return this._renderContentButtonGroup(item.buttons, item.align);
                    }
                    return '';
                });
                this.settings.body = html`<div class="gm-form">${renderedItems}</div>`;
                this.settings._bodyFragments = [];
            } else {
                this.settings.body = this._generateFormHtml(this.settings.fields);
            }
            
            if (!this.settings.isBatch && !this.settings.buttons) {
                if (this.settings.readOnly) {
                    this.settings.buttons = [
                        {
                            text: 'CLOSE',
                            class: 'gm-btn-secondary',
                            close: true
                        }
                    ];
                } else {
                    this.settings.buttons = [
                        {
                            text: this.settings.submitText || 'SAVE',
                            class: 'gm-btn-warning gm-btn-submit',
                            onClick: (_snapshot, $body, $modal) => {
                                const { isValid, data } = this._validateForm($body, this.settings.fields);
                                if (!isValid) return false;
                                if (typeof this.settings.onSubmit === 'function') {
                                    return this.settings.onSubmit(data, $body, $modal);
                                }
                            }
                        },
                        {
                            text: this.settings.cancelText || 'CANCEL',
                            class: 'gm-btn-secondary',
                            close: true
                        }
                    ];
                }
            }
        }

        this._injectBody();
        this._attachFormFieldAccess();

        if (this.settings.isBatch && this.settings.batchColumns) {
            this._populateBatchTable();
        }

        this._setupButtons();

        $modal.on('shown.bs.modal', () => {
            if (this.settings._tableActions) {
                this.settings._tableActions.forEach(action => {
                    const finalSelector = action.selector.startsWith('.') || action.selector.startsWith('#')
                        ? `${action.selector}, [data-action="${action.selector.replace('.', '').replace('gm-btn-', '')}"]`
                        : `[data-action="${action.selector}"], ${action.selector}`;

                    $modal.on('click', `#${action.tableId} ${finalSelector}`, (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const $btn = $(e.currentTarget);
                        const $tr = $btn.closest('tr');
                        const tableSelector = '#' + action.tableId;
                        let data = null;

                        if (window.DataTableAdapter && typeof window.DataTableAdapter.getRowData === 'function') {
                            data = window.DataTableAdapter.getRowData(tableSelector, $tr);
                        } else if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableSelector)) {
                            data = $(tableSelector).DataTable().row($tr).data();
                        }

                        if (!data) {
                            data = $tr.find('td').map(function() { return $(this).text(); }).get();
                        }

                        if (typeof action.callback === 'function') {
                            action.callback(data, $tr, e);
                        }
                    });
                });
            }

            // ── Input group action buttons ──
            if (this.settings.fields && !this.settings.readOnly) {
                const allFields = this._getAllFields();
                const hasInputActions = allFields.some(f =>
                    (Array.isArray(f.append) && f.append.some(a => a.type === 'button' && typeof a.onClick === 'function')) ||
                    (Array.isArray(f.prepend) && f.prepend.some(a => a.type === 'button' && typeof a.onClick === 'function'))
                );
                if (hasInputActions) {
                    $modal.on('click', '[data-gm-input-action]', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const key = $(e.currentTarget).data('gm-input-action');
                        // Find the field config that has this action
                        for (const f of allFields) {
                            const items = [...(f.prepend || []), ...(f.append || [])];
                            const action = items.find(a => a.key === key && typeof a.onClick === 'function');
                            if (action) {
                                const data = this._getFormData();
                                const result = action.onClick(data, this.elements.$body, this.elements.$modal);
                                if (result === false) return;
                                break;
                            }
                        }
                    });
                }
            }
            
            if (this.settings.fields && !this.settings.readOnly) {
                this._initDatepickerFields();
                this._initValidationListeners();
                this._checkFormValidity(true);
            }

            // Bind click listeners for contentButton and contentButtonGroup items
            if (this.settings.items && this.settings.items.length > 0) {
                const contentButtons = [];
                this.settings.items.forEach(item => {
                    if (item.type === 'contentButton' && item.button) {
                        contentButtons.push(item.button);
                    } else if (item.type === 'contentButtonGroup' && Array.isArray(item.buttons)) {
                        contentButtons.push(...item.buttons);
                    }
                });

                contentButtons.forEach(btn => {
                    if (typeof btn.onClick === 'function') {
                        $modal.find(`#${btn.id}`).on('click', (e) => {
                            btn.onClick(e, this, this._builder);
                            if (btn.close) {
                                this.hide();
                            }
                        });
                    }
                });
            }

            if (this.settings.onShown) {
                this.settings.onShown(this.elements.$body);
            }
            if (this.settings._onShownHooks && this.settings._onShownHooks.length > 0) {
                this.settings._onShownHooks.forEach(hook => {
                    if (typeof hook === 'function') hook(this.elements.$body);
                });
            }
        });

        $modal.on('hidden.bs.modal', () => {
            this._destroyDatepickerFields();

            if (this.settings.onHidden) {
                this.settings.onHidden();
            }
            if (this.settings._onHiddenHooks && this.settings._onHiddenHooks.length > 0) {
                this.settings._onHiddenHooks.forEach(hook => {
                    if (typeof hook === 'function') hook();
                });
            }
            $modal.remove();
        });

        if (window.bootstrap && bootstrap.Modal) {
            this.bsModal = new bootstrap.Modal($modal[0], {
                backdrop: this.settings.backdrop,
                keyboard: this.settings.keyboard
            });
            this.bsModal.show();
            $modal.data('bs.modal', this.bsModal);
        } else if ($.fn.modal) {
            $modal.modal({
                backdrop: this.settings.backdrop,
                keyboard: this.settings.keyboard,
                show: true
            });
        } else {
            $modal.addClass('show').css('display', 'block');
        }

        return this;
    }

    hide() {
        const $ = window.jQuery;
        if (this.bsModal) {
            this.bsModal.hide();
        } else if ($.fn.modal) {
            this.elements.$modal.modal('hide');
        }
    }

    _generateReadOnlyFieldHtml(field) { return _generateReadOnlyFieldHtmlFn(this, field); }

    _renderSingleContentButton(btn) {
        const style = btn.color ? `background-color:${btn.color};color:${btn.textColor || '#fff'};` : '';
        const iconHtml = btn.icon ? html`<i class="${btn.icon} me-1"></i>` : '';
        
        let containerClass = 'd-flex mb-3 justify-content-start';
        let btnClass = `gm-btn ${btn.class}`;

        if (btn.align === 'center') {
            containerClass = 'd-flex mb-3 justify-content-center';
        } else if (btn.align === 'right') {
            containerClass = 'd-flex mb-3 justify-content-end';
        } else if (btn.align === 'full') {
            containerClass = 'mb-3';
            btnClass += ' w-100 d-block';
        }

        return html`
            <div class="${containerClass}">
                <button type="button" id="${btn.id}" class="${btnClass}" style="${style}">
                    ${iconHtml}${btn.text}
                </button>
            </div>`;
    }

    _renderContentButtonGroup(buttons, align = 'left') {
        let alignClass = 'justify-content-start';
        let isFill = false;

        if (align === 'right') {
            alignClass = 'justify-content-end';
        } else if (align === 'center') {
            alignClass = 'justify-content-center';
        } else if (align === 'between') {
            alignClass = 'justify-content-between';
        } else if (align === 'fill') {
            alignClass = 'w-100';
            isFill = true;
        }

        const btnsHtml = buttons
            .filter(btn => {
                if (typeof btn.showIf === 'function') return Boolean(btn.showIf(this));
                return btn.showIf !== false;
            })
            .map(btn => {
                const style = btn.color ? `background-color:${btn.color};color:${btn.textColor || '#fff'};` : '';
                const iconHtml = btn.icon ? html`<i class="${btn.icon} me-1"></i>` : '';
                const fillClass = isFill ? 'flex-fill' : '';
                return html`
                <button type="button" id="${btn.id}" class="gm-btn ${btn.class} ${fillClass}" style="${style}">
                    ${iconHtml}${btn.text}
                </button>`;
            });

        return html`<div class="d-flex flex-wrap gap-2 mb-3 ${alignClass}">${btnsHtml}</div>`;
    }

    _renderReadonlyRow(id, label, displayValue, field) { return _renderReadonlyRowFn(this, id, label, displayValue, field); }

    _generateFieldHtml(field) { return _generateFieldHtmlFn(this, field); }

    _generateFormHtml(fields) { return _generateFormHtmlFn(this, fields); }

    _initDatepickerFields() { return _initDatepickerFieldsFn(this); }

    _applyDatepickerFieldConfig(builder, field, $input) { return _applyDatepickerFieldConfigFn(this, builder, field, $input); }

    _destroyDatepickerFields() { return _destroyDatepickerFieldsFn(this); }

    _initValidationListeners() { return _initValidationListenersFn(this); }

    _validateField(field, showUI = false, val = null) { return _validateFieldFn(this, field, showUI = false, val = null); }

    _updateFieldUI(id, error, val = null) { return _updateFieldUIFn(this, id, error, val = null); }

    _checkFormValidity(silent = true) { return _checkFormValidityFn(this, silent = true); }

    _validateForm($body, fields) { return _validateFormFn(this, $body, fields); }

    _getFormData() { return _getFormDataFn(this); }

    /**
     * Exposes field handles and a live data snapshot on $body:
     *   $body.fields.fieldId  → jQuery input(s)
     *   $body.gmData()        → current form values by field id
     */
    _attachFormFieldAccess() { return _attachFormFieldAccessFn(this); }

    _applyStyles() {
        const { $dialog, $content, $body, $header, $footer } = this.elements;
        const s = this.settings;

        if (s.size) $dialog.addClass('modal-' + s.size);
        if (s.width) $dialog.css({ width: s.width, maxWidth: 'none' });
        if (s.height) $content.css('height', s.height);

        $dialog.css(s.dialogStyle);
        $content.css(s.contentStyle);
        $body.css(s.bodyStyle);
        $header.css(s.headerStyle);
        $footer.css(s.footerStyle);
    }

    _configureHeader() {
        const { $header, $icon, $titleText } = this.elements;
        const s = this.settings;
        const typeConfig = ModalTypes[s.type] || ModalTypes.warning;

        if (!s.header) {
            $header.hide();
        } else {
            $header.css('background-color', typeConfig.color);
            
            if (isTrusted(s.title)) {
                $titleText.html(s.title);
            } else {
                $titleText.text(s.title || '');
            }

            if (typeConfig.icon) $icon.addClass(typeConfig.icon).show();
            else $icon.hide();
        }
    }

    _configureFooter() {
        const { $footer } = this.elements;
        const s = this.settings;
        if (!s.footer) {
            $footer.hide();
        } else {
            const alignmentMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
            $footer.css('justify-content', alignmentMap[s.footerAlign] || 'flex-end');
        }
    }

    _mapButtonClass(bsClass) {
        if (!bsClass || typeof bsClass !== 'string') return 'gm-btn-secondary';
        const map = {
            'btn-secondary': 'gm-btn-secondary',
            'btn-primary': 'gm-btn-primary',
            'btn-warning': 'gm-btn-warning',
            'btn-danger': 'gm-btn-primary',
            'btn-info': 'gm-btn-primary',
            'btn-success': 'gm-btn-primary'
        };

        const parts = bsClass.trim().split(/\s+/).filter(Boolean);
        const mappedParts = parts.map(p => map[p] || p);

        const hasThemeClass = mappedParts.some(p => p.startsWith('gm-btn-') && p !== 'gm-btn-fluid');
        if (!hasThemeClass) {
            mappedParts.unshift('gm-btn-secondary');
        }

        return mappedParts.join(' ');
    }

    _injectBody() {
        const { $body } = this.elements;
        const $ = window.jQuery;
        let b = this.settings.body;
        let fragments = this.settings._bodyFragments || [];
        
        // Check if the primary body is trusted
        const bodyIsTrusted = b === undefined || b === null || b === '' || isTrusted(b);
        // Check if ALL fragments are trusted
        const allFragmentsTrusted = fragments.every(f => isTrusted(f));

        if (b instanceof $ || (b && b.nodeType)) {
            $body.empty().append(b);
            if (fragments.length > 0) $body.append(fragments.join(''));
        } else {
            const combined = html`${b || ''}${fragments}`;
            $body.html(combined);
        }
    }

    _populateBatchTable() { return _populateBatchTableFn(this); }

    async _processBatch() {
        if (this.processingState.active) return;
        this.processingState.active = true;

        const { $body, $acceptBtn, $rejectBtn } = this.elements;

        $acceptBtn.prop('disabled', true).html('<span class="gm-spinner"></span> Processing...');
        $rejectBtn.prop('disabled', true);

        const formData = this.settings.fields ? this._getFormData() : {};

        for (let i = 0; i < this.settings.items.length; i++) {
            const item = this.settings.items[i];
            const $statusCell = $body.find(`tr[data-item-index="${i}"] .gm-batch-status`);
            $statusCell.html('<span class="gm-spinner gm-text-primary"></span>');

            try {
                const result = await this.settings.processAction(item, formData, i);
                $statusCell.html('<span class="gm-badge gm-badge-success"><i class="bi bi-check-circle"></i> SUCCESS</span>');
                this.processingState.results.push({ item, success: true, result });
            } catch (error) {
                const errorMsg = typeof error === 'string' ? error : (error.message || 'Error occurred');
                $statusCell.html(`<span class="gm-badge gm-badge-danger" title="${errorMsg}"><i class="bi bi-x-circle"></i> FAILED</span>`);
                this.processingState.results.push({ item, success: false, error: errorMsg });
            }
        }

        this.processingState.active = false;
        $acceptBtn.hide();
        $rejectBtn.text('CLOSE').prop('disabled', false).show();

        if (typeof this.settings.onAccept === 'function') {
            this.settings.onAccept(this.processingState.results);
        }
    }

    _setupButtons() {
        const { $footer, $body, $modal, $acceptBtn, $rejectBtn } = this.elements;
        const $ = window.jQuery;
        const s = this.settings;
        if (!s.footer) return;

        if (Array.isArray(s.buttons) && s.buttons.length > 0) {
            $footer.empty();
            s.buttons.forEach(btn => {
                if (typeof btn.showIf === 'function' && !btn.showIf(this)) return;
                if (btn.showIf === false) return;

                const $btn = $(html`<button type="button" class="gm-btn gm-btn-lg ${this._mapButtonClass(btn.class || 'btn-secondary')}">${btn.text || 'Button'}</button>`)
                    .css({
                        'padding': '0.7rem 2rem',
                        'font-weight': '600',
                        'border-radius': '8px',
                        'font-size': '0.95rem',
                        'border': 'none'
                    })
                    .appendTo($footer);

                $btn.on('click', () => {
                    if (typeof btn.onClick === 'function') {
                        const data = this.settings.fields ? this._getFormData() : {};
                        const result = btn.onClick(data, $body, $modal);
                        if (result === false) return;
                    }
                    if (btn.close !== false) this.hide();
                });
            });
        } else {
            if (s.showAccept) {
                const typeColor = (ModalTypes[s.type] || ModalTypes.warning).color;
                $acceptBtn.text(s.acceptText).css({
                    'background-color': typeColor,
                    'color': 'white',
                    'border': 'none',
                    'padding': '0.7rem 2rem',
                    'font-weight': '600',
                    'border-radius': '8px',
                    'font-size': '0.95rem'
                }).show();
                
                $acceptBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    if (s.isBatch && s.processAction) {
                        if (s.fields) {
                            const { isValid } = this._validateForm($body, s.fields);
                            if (!isValid) return;
                        }
                        this._processBatch();
                        return;
                    }

                    if (typeof s.onAccept === 'function') {
                        const result = s.onAccept($body);
                        if (result === false) return;
                    }
                    this.hide();
                });
            } else {
                $acceptBtn.hide();
            }

            if (s.showReject) {
                $rejectBtn.text(s.rejectText).css({
                    'padding': '0.7rem 2rem',
                    'font-weight': '600',
                    'border-radius': '8px',
                    'font-size': '0.95rem'
                }).show();
                $rejectBtn.off('click').on('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    if (typeof s.onReject === 'function') {
                        const result = s.onReject($body);
                        if (result === false) return;
                    }
                    this.hide();
                });
            } else {
                $rejectBtn.hide();
            }
        }
    }
}

GenericModal.types = ModalTypes;
GenericModal.defaults = ModalDefaults;
GenericModal.actionConfigs = ActionConfigs;
GenericModal.Actions = Object.freeze(
    Object.keys(ActionConfigs).reduce((acc, key) => {
        acc[key.toUpperCase()] = key;
        return acc;
    }, {})
);
GenericModal.Sizes = Sizes;
GenericModal.Types = Types;
GenericModal.FieldTypes = FieldTypes;
GenericModal.Align = Align;
GenericModal.Backdrop = Backdrop;
GenericModal.ActionSizes = ActionSizes;
GenericModal.Validators = Validators;
GenericModal.Colors = Colors;
GenericModal.ReadOnlyStyles = ReadOnlyStyles;

export const Modal = {
    _activeLoadingModal: null,
    _loadingHidePending: false,

    create: function (options) {
        return GenericModal.create(options);
    },

    from: function (target, options) {
        return GenericModal.from(target, options);
    },

    show: function (options) {
        return GenericModal.create(options).show();
    },

    info: function (title, body, options = {}) {
        const builder = this.create(options).asInfo().withTitle(title).withBody(body);
        return options.autoShow === false ? builder : builder.show();
    },

    warning: function (title, body, options = {}) {
        const builder = this.create(options).asWarning().withTitle(title).withBody(body);
        return options.autoShow === false ? builder : builder.show();
    },

    error: function (body, options = {}) {
        const builder = this.create(options).asError().withBody(body);
        return options.autoShow === false ? builder : builder.show();
    },

    success: function (title, body, options = {}) {
        const builder = this.create(options).asSuccess().withTitle(title).withBody(body);
        return options.autoShow === false ? builder : builder.show();
    },

    confirm: function (title, body, onAccept, options = {}) {
        const builder = this.create(options).asConfirm().withTitle(title).withBody(body);
        if (typeof onAccept === 'function') builder.onAccept(onAccept);
        return options.autoShow === false ? builder : builder.show();
    },

    accessDenied: function (featureName, options = {}) {
        const body = html`You have no access rights to <b>${featureName}</b> functionality.<br><br>To verify, please contact <b>Security Administrator</b>`;
        return this.error(body, { title: 'Access Denied', ...options });
    },

    confirmAsync: function (title, body, options = {}) {
        return new Promise((resolve) => {
            let resolved = false;
            const builder = GenericModal.create({
                ...options,
                title,
                body,
                onAccept: () => { resolved = true; resolve(true); },
                onReject: () => { resolved = true; resolve(false); },
                onHidden: () => { if (!resolved) resolve(false); }
            }).asConfirm();
            
            if (options.acceptText) builder.withAcceptText(options.acceptText);
            if (options.rejectText) builder.withRejectText(options.rejectText);
            
            builder.show();
        });
    },

    form: function (options = {}) {
        const builder = this.create(options).asForm();
        return options.autoShow === false ? builder : builder.show();
    },

    _getLoadingBsInstance: function ($modal) {
        if (!$modal || !$modal.length) return null;
        const el = $modal[0];
        return $modal.data('bs.modal')
            || (window.bootstrap && bootstrap.Modal && typeof bootstrap.Modal.getInstance === 'function'
                ? bootstrap.Modal.getInstance(el)
                : null);
    },

    _isLoadingTransitioning: function ($modal) {
        const bs = this._getLoadingBsInstance($modal);
        if (!bs) return false;
        // Bootstrap 5: _isTransitioning; Bootstrap 4 jQuery plugin: transitioning
        return !!(bs._isTransitioning || bs.transitioning);
    },

    _isLoadingFullyShown: function ($modal) {
        if (!$modal || !$modal.length) return false;
        // BS5 uses .show; BS4 uses .in. Class can be present mid-transition —
        // pair with !_isLoadingTransitioning before calling hide().
        return $modal.hasClass('show') || $modal.hasClass('in');
    },

    _cleanupOrphanLoadingModals: function () {
        const $ = window.jQuery;
        if (!$ || typeof document === 'undefined') return;

        $('.gm-loading-modal').each((_, el) => {
            const $el = $(el);
            const bs = this._getLoadingBsInstance($el);
            try {
                if (bs && typeof bs.dispose === 'function') bs.dispose();
                else if (bs && typeof bs.hide === 'function') bs.hide();
                else if (typeof $el.modal === 'function') $el.modal('hide');
            } catch (_) { /* ignore */ }
            $el.remove();
        });

        // Only clear page lock / backdrops when nothing else is open
        if (!$('.modal.show, .modal.in').length) {
            $('.modal-backdrop').remove();
            $('body').removeClass('modal-open').css({ overflow: '', 'padding-right': '' });
        }
    },

    _hideLoadingNow: function ($modal) {
        if (!$modal || !$modal.length) return;
        const bs = this._getLoadingBsInstance($modal);
        if (bs && typeof bs.hide === 'function') bs.hide();
        else if (typeof $modal.modal === 'function') $modal.modal('hide');
        // Keep _activeLoadingModal until hidden.bs.modal removes the node.
    },

    showLoading: function (options = {}) {
        const $ = window.jQuery;
        // A new show cancels any deferred hide from a prior fast request.
        this._loadingHidePending = false;

        if (this._activeLoadingModal && this._activeLoadingModal.length && this._activeLoadingModal.is(':visible')) {
            if (options.message) this._activeLoadingModal.find('.gm-loading-message').text(options.message);
            return this._activeLoadingModal;
        }

        // Drop stale nodes left behind by earlier hide-during-show races
        this._cleanupOrphanLoadingModals();
        this._activeLoadingModal = null;

        const settings = { message: 'Loading, please wait...', ...options };
        const id = 'gmLoading_' + Math.floor(Math.random() * 1000000);
        // No `fade` class: loading overlays should appear/disappear immediately.
        // Fade animations are what make Bootstrap ignore hide() during show.
        const template = html`
            <div id="${id}" class="modal gm-modal gm-loading-modal" tabindex="-1" role="dialog">
                <div class="modal-dialog gm-dialog">
                    <div class="modal-content gm-content">
                        <div class="gm-loading-body">
                            <div class="gm-pulsing-dot"></div>
                            <p class="gm-loading-message">${settings.message}</p>
                        </div>
                    </div>
                </div>
            </div>`;

        const $modal = $(template).appendTo('body');
        const el = $modal[0];

        const onShown = () => {
            // Bootstrap ignores hide() while showing — retry after show completes.
            if (this._loadingHidePending && this._activeLoadingModal && this._activeLoadingModal.attr('id') === id) {
                this._loadingHidePending = false;
                // Defer one macrotask so we are outside Bootstrap's shown call stack.
                setTimeout(() => {
                    if (this._activeLoadingModal && this._activeLoadingModal.attr('id') === id) {
                        this._hideLoadingNow($modal);
                    }
                }, 0);
            }
        };

        const onHidden = () => {
            el.removeEventListener('shown.bs.modal', onShown);
            el.removeEventListener('hidden.bs.modal', onHidden);
            $modal.remove();
            if (this._activeLoadingModal && this._activeLoadingModal.attr('id') === id) this._activeLoadingModal = null;
            this._loadingHidePending = false;
        };

        // Native listeners: reliable with Bootstrap 5 (does not depend on jQuery event wiring).
        el.addEventListener('shown.bs.modal', onShown);
        el.addEventListener('hidden.bs.modal', onHidden);

        if (window.bootstrap && bootstrap.Modal) {
            const bs = new bootstrap.Modal(el, { backdrop: 'static', keyboard: false });
            bs.show();
            $modal.data('bs.modal', bs);
        } else {
            $modal.modal({ backdrop: 'static', keyboard: false, show: true });
        }

        this._activeLoadingModal = $modal;
        return $modal;
    },

    hideLoading: function () {
        if (!this._activeLoadingModal || !this._activeLoadingModal.length) {
            // Recover stuck overlays when the reference was cleared mid-transition
            this._cleanupOrphanLoadingModals();
            this._loadingHidePending = false;
            return;
        }

        const $modal = this._activeLoadingModal;

        // Hide during show fade is a no-op in Bootstrap — defer until shown.bs.modal
        if (this._isLoadingTransitioning($modal) || !this._isLoadingFullyShown($modal)) {
            this._loadingHidePending = true;
            return;
        }

        this._loadingHidePending = false;
        this._hideLoadingNow($modal);

        // Safety net: clean up any orphaned backdrops after hiding
        this._cleanupOrphanLoadingModals();
    }
};

// Mirror public enums onto Modal so consumers can use Modal.* only
Modal.Sizes = Sizes;
Modal.Types = Types;
Modal.FieldTypes = FieldTypes;
Modal.Align = Align;
Modal.Backdrop = Backdrop;
Modal.ActionSizes = ActionSizes;
Modal.Validators = Validators;
Modal.Colors = Colors;
Modal.ReadOnlyStyles = ReadOnlyStyles;
